'use client';

import { Box, Flex, freddyTheme, SpriteIcon, Typography } from '@coolblue-development/freddy';
import type { InstallationServiceData } from '@coolblue-development/next-installation-services';
import {
  installationServiceSelectedEvent,
  InstallationServicesEmitter
} from '@coolblue-development/next-installation-services/client';
import { GetDefaultExtendedUsps } from '@features/product/components/OrderBlock/ExtendedUsps/GetDefaultExtendedUsps';
import { GetPreSelectedServiceUsps } from '@features/product/components/OrderBlock/ExtendedUsps/GetPreSelectedServiceUsps';
import { StyledBox } from '@features/product/components/OrderBlock/ExtendedUsps/StyledComponents';
import { Product } from '@features/product/typings';
import { ReactElement, useEffect, useRef, useState } from 'react';

import { ExtendedUsp } from './types';

export const ExtendedUsps = ({
  productTypeId,
  properties,
  services,
  dateOverride
}: {
  productTypeId: number;
  properties: Product['properties'];
  services: InstallationServiceData[] | null;
  dateOverride?: string;
}): ReactElement => {
  const firstRender = useRef(true);

  const defaultExtendedUsps = GetDefaultExtendedUsps(productTypeId, properties, dateOverride);

  const [extendedUspsState, setExtendedUspsState] = useState<ExtendedUsp[]>(
    defaultExtendedUsps.length > 0 ? defaultExtendedUsps : GetPreSelectedServiceUsps(services)
  );

  useEffect(() => {
    firstRender.current = false;
  }, []);

  useEffect(() => {
    const handleServiceSelected = ({
      extendedUsps,
      selected
    }: {
      serviceId: number;
      extendedUsps: ExtendedUsp[];
      selected: boolean;
    }) => {
      if (!selected) {
        setExtendedUspsState(defaultExtendedUsps);
        return;
      }

      if (extendedUspsState === extendedUsps || !extendedUsps || extendedUsps.length === 0) {
        return;
      }

      setExtendedUspsState(extendedUsps);
    };

    InstallationServicesEmitter.on(installationServiceSelectedEvent, handleServiceSelected);

    return () => {
      InstallationServicesEmitter.off(installationServiceSelectedEvent, handleServiceSelected);
    };
  }, [defaultExtendedUsps, extendedUspsState, productTypeId, properties]);

  const BoxComponent = firstRender.current ? Box : StyledBox;

  if (!extendedUspsState.length) {
    return <></>;
  }

  return (
    <BoxComponent key={JSON.stringify(extendedUspsState)}>
      {extendedUspsState.map((usp, index) => (
        <Box key={index}>
          <Flex gap={200} mt={index !== 0 ? '1rem' : 0}>
            <SpriteIcon name='checkmark' color='sentimentPositive' />

            <Typography
              mt='-0.4rem'
              variant='body'
              fontWeight={freddyTheme.typography.h2.fontWeight}
            >
              {usp.title}
            </Typography>
          </Flex>
          <Typography variant='body' mt={0} mb={0} ml={600}>
            {usp.description}
          </Typography>
        </Box>
      ))}
    </BoxComponent>
  );
};
